import type { TFunction } from "i18next"

import type { ProjectScheduleHealth, ScheduleState } from "../types/types.ts"
import { PROJECT_STATUSES_OBJ } from "./project-statuses.ts"

export type HealthTone = "success" | "warning" | "danger" | "neutral"

// The states a user can filter by. Not applicable is left out: it only ever
// describes a closed project, which the Closed quick view already isolates.
export const SCHEDULE_FILTER_STATES: ScheduleState[] = [
  "on_schedule",
  "due_soon",
  "late",
  "missing_schedule",
  "not_started"
]

const UNDERWAY_STATUSES = new Set([
  PROJECT_STATUSES_OBJ[3]?.label,
  PROJECT_STATUSES_OBJ[4]?.label
])

/** A project still being planned has nothing yet to fall behind on, so its
 *  missing schedule is neutral; an underway one without dates is an exception. */
export function scheduleTone(
  health: ProjectScheduleHealth,
  projectStatus?: string | null
): HealthTone {
  switch (health.state) {
    case "on_schedule":
      return "success"
    case "due_soon":
      return "warning"
    case "late":
      return "danger"
    case "missing_schedule":
      return UNDERWAY_STATUSES.has(projectStatus ?? "") ? "danger" : "neutral"
    default:
      return "neutral"
  }
}

export function scheduleLabel(
  t: TFunction,
  health: ProjectScheduleHealth
): string {
  const days = health.days ?? 0
  switch (health.state) {
    case "due_soon":
      if (days === 0) return t("projectsCommand.schedule.dueToday")
      if (days === 1) return t("projectsCommand.schedule.dueTomorrow")
      return t("projectsCommand.schedule.dueIn", { count: days })
    case "late":
      return t("projectsCommand.schedule.late", { count: days })
    default:
      return t(`projectsCommand.schedule.${health.state}`)
  }
}

export function scheduleFilterLabel(t: TFunction, state: ScheduleState) {
  return t(`projectsCommand.scheduleFilter.${state}`)
}

export const HEALTH_DOT_CLASS: Record<HealthTone, string> = {
  success: "bg-green-600",
  warning: "bg-amber-500",
  danger: "bg-red-600",
  neutral: "bg-slate-400"
}

export const HEALTH_TEXT_CLASS: Record<HealthTone, string> = {
  success: "text-green-800",
  warning: "text-amber-800",
  danger: "text-red-700",
  neutral: "text-slate-600"
}
